import {
  createTheme,
  type Theme,
  type ThemeOptions,
} from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";
import { alpha } from "@mui/material/styles";

import "./theme/types";
import { productBrand } from "./productBrand";

const APP_BORDER_COLOR_LIGHT = alpha("#1A1930", 0.1);

/** @deprecated Use `theme.palette.surfaceMuted` in theme-aware code. */
export const SURFACE_MUTED = "#F8F8F8";

export const SURFACE_MUTED_LIGHT = "#F8F8F8";
/** Dark: warm muted surface (drawers, hover panels) — derived from `#08070B`, not blue-gray. */
export const SURFACE_MUTED_DARK = "#17161C";
/** Dark: nav sidebar only — darker than `background.default` (`#08070B`). */
export const SIDEBAR_SURFACE_DARK = "#050408";

// Theme constants
export const themeConstants = {
  border: {
    radius: "8px",
    /** Light-mode default; prefer `theme.palette.divider` in components. */
    color: APP_BORDER_COLOR_LIGHT,
    width: "1px",
  },
  input: {
    radius: "8px",
  },
  shadow: {
    none: "0px 0px 0px 0px rgba(245, 245, 245, 1)",
  },
  palette: {
    primary: {
      main: "#1A1930",
      light: "#2F2E4D",
      dark: "#0F0E1D",
      alpha: alpha("#1A1930", 0.2),
    },
  },
  filters: {
    matte: {
      default:
        "brightness(0) saturate(100%) invert(10%) sepia(47%) saturate(620%) hue-rotate(203deg) brightness(95%) contrast(99%)",
      hover: "grayscale(0%) brightness(100%) contrast(100%) saturate(100%)",
    },
  },

  image: {
    container: (theme: Theme) => ({
      overflow: "hidden",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexShrink: 0,
      borderRadius: "16px",
      border: `0px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      position: "relative",
      aspectRatio: "5/4",
      width: "100%",
      "&:hover": {
        "& .image-element": {
          filter:
            "grayscale(0%) brightness(100%) contrast(100%) saturate(100%)",
        },
        "& .color-overlay": {
          opacity: 0,
        },
      },
    }),
    element: {
      objectFit: "cover",
      width: "100%",
      height: "100%",
      filter: "grayscale(40%)",
      transition: "all 0.3s ease-in-out",
    },
  },
} as const;

const brandPalettes = {
  spotify: {
    main: "#1DB954",
    light: "#1ED760",
    dark: "#1AA34A",
  },
  youtube: {
    main: "#FF0000",
    light: "#FF3333",
    dark: "#CC0000",
  },
  instagram: {
    main: "#E4405F",
    light: "#ED6B85",
    dark: "#B83350",
  },
  facebook: {
    main: "#1877F2",
    light: "#4B9AF5",
    dark: "#0C5FBF",
  },
  tiktok: {
    main: "#25F4EE",
    light: "#5FF7F2",
    dark: "#1BC4BF",
  },
  podscope: {
    main: "#7056EB",
    light: "#9B8AE8",
    dark: "#4A3AB8",
  },
} as const;

function buildPalette(mode: PaletteMode): ThemeOptions["palette"] {
  if (mode === "dark") {
    /** Dark base (user-defined); surfaces stay warm neutrals, not blue-violet. */
    const darkBase = "#08070B";
    const darkPaper = "#121016";
    /** Muted warm gray — secondary text, `primary.dark`, footer bar. */
    const warmMuted = "#9E9A94";

    return {
      mode: "dark",
      primary: {
        /** White so `color="primary"` text/icons match `text.primary`. */
        main: "#FFFFFF",
        light: alpha("#FFFFFF", 0.14),
        dark: warmMuted,
        contrastText: darkBase,
      },
      secondary: {
        main: "#FF6B6B",
        light: "#FFA5A5",
        dark: "#CC5555",
      },
      neutral: {
        100: "#141318",
        200: "#1C1B1F",
        300: "#26252A",
        400: "#323036",
        500: "#48464C",
        600: "#6A6768",
        700: "#908C88",
        800: "#B8B3AD",
        900: "#E8E4DD",
        main: "#6A6768",
        dark: "#141318",
        light: "#E8E4DD",
      },
      background: {
        default: darkBase,
        paper: darkPaper,
      },
      text: {
        primary: "#EDEDE9",
        secondary: warmMuted,
      },
      divider: alpha("#FFFFFF", 0.1),
      surfaceMuted: SURFACE_MUTED_DARK,
      sidebarSurface: SIDEBAR_SURFACE_DARK,
      footerBar: {
        background: "#08070B",
        color: "#EDEDE9",
        linkHover: "#FFFFFF",
      },
      ...brandPalettes,
    };
  }

  return {
    mode: "light",
    primary: {
      main: productBrand.lightPrimary.main,
      light: productBrand.lightPrimary.light,
      dark: productBrand.lightPrimary.dark,
    },
    secondary: {
      main: "#FF6B6B",
      light: "#FFA5A5",
      dark: "#CC5555",
    },
    neutral: {
      100: "#F7F7F8",
      200: "#E8E8E9",
      300: "#DCDCDD",
      400: "#BDBDBE",
      500: "#9E9EA0",
      600: "#7E7E80",
      700: "#5F5F61",
      800: "#3F3F41",
      900: "#1C1C1E",
      main: "#9E9EA0",
      dark: "#1C1C1E",
      light: "#F7F7F8",
    },
    background: {
      default: "#FBFBFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: productBrand.textPrimary,
      secondary: "#666666",
    },
    divider: APP_BORDER_COLOR_LIGHT,
    surfaceMuted: SURFACE_MUTED_LIGHT,
    sidebarSurface: SURFACE_MUTED_LIGHT,
    footerBar: {
      background: "#FFFFFF",
      color: productBrand.textPrimary,
      linkHover: productBrand.lightPrimary.main,
    },
    ...brandPalettes,
  };
}

export function createAppTheme(mode: PaletteMode = "light"): Theme {
  const palette = buildPalette(mode);

  return createTheme({
    palette,
    typography: {
      fontFamily: productBrand.fontFamily,
      h1: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "2.6rem",
        fontWeight: productBrand.id === "conscope" ? 600 : 800,
        lineHeight: 1.4,
        overflow: "visible",
        "@media (max-width:600px)": {
          fontSize: "2rem",
        },
      },
      h2: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "1.3rem",
        fontWeight: productBrand.headingFontWeight,
        lineHeight: 1.1,
        overflow: "visible",
      },
      h3: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "1.1rem",
        fontWeight: productBrand.headingFontWeight,
        lineHeight: 1,
        overflow: "visible",
      },
      h4: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "1.25rem",
        fontWeight: 400,
        lineHeight: 1,
        overflow: "visible",
      },
      h5: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1,
        overflow: "visible",
      },
      h6: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 0.9,
        overflow: "visible",
      },
      subtitle1: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.5,
        overflow: "visible",
      },
      subtitle2: {
        fontFamily: productBrand.headingFontFamily,
        fontSize: "0.75rem",
        fontWeight: 200,
        lineHeight: 1.57,
        letterSpacing: "0.5px",
        overflow: "visible",
      },
      body1: {
        fontFamily: productBrand.fontFamily,
        fontWeight: productBrand.bodyFontWeight,
        fontSize: "0.875rem",
        lineHeight: 1.2,
        overflow: "visible",
      },
      body2: {
        fontFamily: productBrand.fontFamily,
        fontSize: "0.8125rem",
        lineHeight: 1.2,
        fontWeight: productBrand.bodyFontWeight,
        overflow: "visible",
      },
      button: {
        fontFamily: productBrand.fontFamily,
        textTransform: "none",
        fontWeight: productBrand.bodyFontWeight,
        fontSize: "0.875rem",
        overflow: "visible",
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          h1: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
          }),
          img: ({ theme }) => ({
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiAvatarGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiAvatar-root": {
              width: 24,
              height: 24,
              fontSize: "0.75rem",
              border: "1px solid",
              borderColor: theme.palette.divider,
              bgcolor: "transparent",
            },
            "& .MuiAvatarGroup-avatar": {
              width: 24,
              height: 24,
              fontSize: "0.75rem",
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: `0 1px 2px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.08)}`,
          }),
        },
        variants: [
          {
            props: { variant: "clickable" },
            style: ({ theme }) => ({
              cursor: "pointer",
              transition: theme.transitions.create("background-color", {
                duration: theme.transitions.duration.short,
              }),
              "&:hover": {
                backgroundColor: theme.palette.surfaceMuted,
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }),
          },
          {
            props: { variant: "section" },
            style: ({ theme }) => ({
              padding: theme.spacing(3),
              borderRadius: theme.spacing(2),
            }),
          },
        ],
      },
      MuiCardMedia: {
        styleOverrides: {
          root: {
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          },
          img: {
            borderRadius: "inherit",
            width: "100%",
            height: "100%",
            objectFit: "contain",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "24px",
            paddingBottom: "24px",
            border: "0px solid",
            "&:last-child": {
              paddingBottom: "20px",
              paddingTop: "20px",
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          title: ({ theme }) => ({
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: 1.4,
            color: theme.palette.text.primary,
            fontFamily: productBrand.headingFontFamily,
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: "0px 0px 0px 0px",
            borderLeft: "none",
            backgroundColor: theme.palette.sidebarSurface,
            border: `${themeConstants.border.width} solid ${theme.palette.divider}`,
            boxShadow: themeConstants.shadow.none,
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.5 : 0.12)}`,
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: productBrand.fontFamily,
            fontWeight: 600,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            gap: "8px",
            padding: "0px",
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "16px",
            paddingRight: "16px",
            // No border here — Select/Menu already border the Paper; a List
            // border would stack as a second inner frame on every dropdown.
            "& > .MuiListItem-root": {
              paddingTop: "8px",
              paddingBottom: "8px",
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: "background-color 0.2s ease",
            borderRadius: themeConstants.border.radius,
            "&:hover": {
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === "dark" ? 0.08 : 0.04,
              ),
              cursor: "pointer",
            },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: "8px",
            "&.Mui-selected": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
              },
            },
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }),
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
            opacity: 1,
            height: themeConstants.border.width,
            borderRadius: "1px",
          }),
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: `0 1px 2px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.08)}`,
          }),
        },
        variants: [
          {
            props: { variant: "section" },
            style: ({ theme }) => ({
              padding: theme.spacing(3),
              borderRadius: theme.spacing(2),
            }),
          },
          {
            props: { variant: "clickable" },
            style: ({ theme }) => ({
              cursor: "pointer",
              transition: theme.transitions.create("background-color", {
                duration: theme.transitions.duration.short,
              }),
              "&:hover": {
                backgroundColor: theme.palette.surfaceMuted,
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }),
          },
        ],
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            "&.Mui-active": {
              color: theme.palette.primary.main,
              "& .MuiTableSortLabel-icon": {
                color: theme.palette.primary.main,
              },
            },
            "&:hover": {
              color: theme.palette.primary.main,
            },
          }),
          icon: {
            fontSize: "1rem",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&:not(:last-child)": {
              "& td": {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            },
            "&:last-child": {
              "& td": {
                borderBottom: "none",
              },
            },
          }),
        },
      },
      MuiContainer: {
        styleOverrides: {
          maxWidthLg: {
            paddingLeft: "0px",
            paddingRight: "0px",
            maxWidth: "1200px !important",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: "8px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
            "&.Mui-selected": {
              borderRadius: "8px",
            },
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            "& .MuiTabs-indicator": {
              height: "3px",
              borderRadius: "1.5px",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiOutlinedInput-root": {
              borderRadius: themeConstants.input.radius,
              backgroundColor: theme.palette.background.paper,
              transition: "all 0.2s ease-in-out",
              "& .MuiOutlinedInput-notchedOutline": {
                border: `${themeConstants.border.width} solid ${theme.palette.divider}`,
                transition: "all 0.2s ease-in-out",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            },
          }),
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiOutlinedInput-root": {
              borderRadius: themeConstants.input.radius,
              backgroundColor: theme.palette.background.paper,
              transition: "all 0.2s ease-in-out",
              "& .MuiOutlinedInput-notchedOutline": {
                border: `${themeConstants.border.width} solid ${theme.palette.divider}`,
                transition: "all 0.2s ease-in-out",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: themeConstants.input.radius,
            backgroundColor: theme.palette.background.paper,
            transition: "all 0.2s ease-in-out",
            "& .MuiOutlinedInput-notchedOutline": {
              border: `${themeConstants.border.width} solid ${theme.palette.divider}`,
              transition: "all 0.2s ease-in-out",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: themeConstants.border.width,
            },
          }),
        },
      },
    },
  });
}

export default createAppTheme("light");
